import React, { useState } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import api from "../lib/api";

const Contact = () => {
  const { settings, boardMembers } = useData();
  const SITE = settings || {};
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      toast.success("Message envoyé ! Nous vous répondons rapidement.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez ultérieurement.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Seo title="Contact — L'Élan Danse" description="Contactez L'Élan Danse, école de danse classique et moderne au Perray-en-Yvelines (78)." />

      <section className="py-20 px-6">
        <SectionTitle kicker="Écrivez-nous" title="Contact" subtitle="Une question, une demande, une envie de venir essayer ? N'hésitez pas à nous écrire." />

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
          <div className="bg-blush-light rounded-3xl p-8 md:p-10">
            <h3 className="font-serif-display text-2xl text-stone-800">Coordonnées</h3>
            <div className="section-divider !mx-0 !w-16 my-4" />
            <ul className="space-y-4 text-stone-700 font-serif-display">
              <li className="flex gap-3"><MapPin className="text-deep-rose shrink-0" /> {SITE.address}</li>
              <li className="flex gap-3 items-center"><Phone className="text-deep-rose" /> <a href={`tel:${SITE.phone}`}>{SITE.phone}</a></li>
              <li className="flex gap-3 items-center"><Mail className="text-deep-rose" /> <a href={`mailto:${SITE.email}`}>{SITE.email}</a></li>
            </ul>
            <div className="mt-6 rounded-2xl overflow-hidden border-4 border-white">
              <iframe title="Carte" src="https://www.google.com/maps?q=Le+Perray-en-Yvelines&output=embed" width="100%" height="260" loading="lazy" style={{ border: 0 }} />
            </div>
          </div>
          <form onSubmit={onSubmit} className="bg-white rounded-3xl shadow-sm p-8 md:p-10 space-y-5">
            <div>
              <label className="block text-sm uppercase tracking-widest text-stone-500 font-serif-display mb-2">Nom</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blush" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest text-stone-500 font-serif-display mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blush" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest text-stone-500 font-serif-display mb-2">Message</label>
              <textarea rows="6" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blush" />
            </div>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-coral text-white px-7 py-3 rounded-full font-serif-display tracking-widest uppercase text-sm disabled:opacity-50">
              {submitting ? "Envoi..." : (<>Envoyer <Send size={16} /></>)}
            </button>
          </form>
        </div>
      </section>

      {Array.isArray(boardMembers) && boardMembers.length > 0 && (
        <section className="pb-20 px-6" data-testid="board-section">
          <SectionTitle kicker="L'équipe bénévole" title="Le bureau de l'association" subtitle="Nos membres élus qui font vivre l'école au quotidien." />
          <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
            {boardMembers.map((m, i) => (
              <div key={m.id || i} className="text-center" data-testid={`board-member-${i}`}>
                <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-blush-light shadow-sm mb-3">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-deep-rose font-serif-display text-4xl">
                      {(m.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  )}
                </div>
                <div className="font-serif-display text-stone-800 text-sm sm:text-base leading-tight">{m.name}</div>
                <div className="text-deep-rose text-xs sm:text-sm mt-1 italic">{m.role}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default Contact;
